import FiltersIcon from 'assets/icons/filters.svg'
import classnames from 'classnames'
import { FilterType, ToggleParams } from 'frontend/enums'
import { Filters } from 'frontend/types'
import AudioDescription from './components/AudioDescription'
import ClearButton from './components/ClearButton/ClearButton'
import EducationLevel from './components/EducationLevel'
import Format from './components/Format'
import Language from './components/Language'
import SchoolSubjectAndKnowledgeArea from './components/SchoolSubjectAndKnowledgeArea'
import SignLanguageInterpreted from './components/SignLanguageInterpreted'
import TeacherResources from './components/TeacherResources'
import { ToggleEnableState } from './components/Toggle/Toggle'
import styles from './MobileFilterItems.module.css'
import { SearchParams } from 'backend/services/types/urApiService.types'

interface FilterItemsProps {
  resetFilter: () => void
  isInModal: boolean
  hiddenFilters: FilterType[]
  filters: Filters
  getInteractiveState: (searchResultsExists: boolean) => ToggleEnableState
  handleClickSettingToggle: (key: ToggleParams) => Promise<void>
  handlePanelClick: (incomingType: FilterType) => void
  searchParams: SearchParams
  openPanels: FilterType[]
  executeSearch: (
    params: SearchParams,
    shouldResetAllFilters?: boolean,
  ) => Promise<void>
}

const FilterItems = ({
  resetFilter,
  isInModal,
  hiddenFilters,
  filters,
  getInteractiveState,
  handleClickSettingToggle,
  handlePanelClick,
  searchParams,
  openPanels,
  executeSearch,
}: FilterItemsProps) => {
  const {
    educationLevels,
    subjectTree,
    formats,
    hasAudioDescription,
    hasSignLanguageInterpretation,
    teacherResources,
    languages,
  } = filters
  return (
    <div
      className={classnames(styles.filterItems, {
        [`${styles.isInSidebar}`]: !isInModal,
      })}
    >
      <div className={classnames({ [`${styles.headerWrapper}`]: !isInModal })}>
        <h2 className={styles.header}>
          <FiltersIcon
            aria-hidden
            className={styles.headerIcon}
            focusable="false"
          />
          Filter
        </h2>
        {!isInModal && (
          <ClearButton resetFilter={resetFilter} isInModal={isInModal} />
        )}
      </div>

      <div className={styles.filterToggleWrapper}>
        <AudioDescription
          isInModal={isInModal}
          interactiveState={getInteractiveState(hasAudioDescription)}
          onClick={() =>
            handleClickSettingToggle(ToggleParams.IS_AUDIO_DESCRIBED)
          }
          searchParams={searchParams}
        />
        <SignLanguageInterpreted
          isInModal={isInModal}
          interactiveState={getInteractiveState(hasSignLanguageInterpretation)}
          onClick={() =>
            handleClickSettingToggle(ToggleParams.IS_SIGN_LANGUAGE_INTERPRETED)
          }
          searchParams={searchParams}
        />
      </div>

      {!hiddenFilters?.includes(FilterType.TEACHER_RESOURCE) && (
        <TeacherResources
          teacherResources={teacherResources}
          handlePanelClick={() => handlePanelClick(FilterType.TEACHER_RESOURCE)}
          isOpen={openPanels.includes(FilterType.TEACHER_RESOURCE)}
          searchParams={searchParams}
          updateSearch={executeSearch}
        />
      )}

      {!hiddenFilters?.includes(FilterType.EDUCATION_LEVEL) && (
        <EducationLevel
          educationLevels={educationLevels}
          handlePanelClick={() => handlePanelClick(FilterType.EDUCATION_LEVEL)}
          isOpen={openPanels.includes(FilterType.EDUCATION_LEVEL)}
          searchParams={searchParams}
          updateSearch={executeSearch}
        />
      )}
      {!hiddenFilters?.includes(FilterType.SCHOOL_SUBJECT) && (
        <SchoolSubjectAndKnowledgeArea
          subjectTree={subjectTree}
          handlePanelClick={() => handlePanelClick(FilterType.SCHOOL_SUBJECT)}
          isOpen={openPanels.includes(FilterType.SCHOOL_SUBJECT)}
          searchParams={searchParams}
          updateSearch={executeSearch}
        />
      )}

      {!hiddenFilters?.includes(FilterType.LANGUAGE) && (
        <Language
          handlePanelClick={() => handlePanelClick(FilterType.LANGUAGE)}
          isOpen={openPanels.includes(FilterType.LANGUAGE)}
          languages={languages}
          searchParams={searchParams}
          updateSearch={executeSearch}
        />
      )}
      {!hiddenFilters?.includes(FilterType.FORMAT) && (
        <Format
          formats={formats}
          handlePanelClick={() => handlePanelClick(FilterType.FORMAT)}
          isOpen={openPanels.includes(FilterType.FORMAT)}
          searchParams={searchParams}
          updateSearch={executeSearch}
        />
      )}
    </div>
  )
}

export default FilterItems
